<template>
  <div>
    <template v-if="!isLoading">
      <div
        class="rounded mb-2"
        @mouseover="imageHover = true"
        @mouseleave="imageHover = false"
        :style="{ 'aspect-ratio': aspectRatio }"
      >
        <div
          v-if="selected"
          id="selected-controls"
          class="mt-1 mr-1"
          :class="{ 'pr-1': addRightPadding }"
        >
          <i
            class="fad fa-square-check check-icon"
          />
        </div>
        <img
          :src="content"
          width="100%"
          :class="{ 'selected-img': selected }"
          :style="{ 'aspect-ratio': aspectRatio }"
        >
        <div
          v-if="!selected && imageHover"
          id="img-controls"
          class="mb-2 mr-2"
          :class="{ 'pr-1': addRightPadding }"
        >
          <span id="delete-img">
            <b-tooltip
              v-if="isActiveAsset"
              target="delete-img"
              triggers="hover"
              placement="left"
            >
              Cannot delete active images.
            </b-tooltip>
            <b-button
              size="sm"
              variant="danger"
              data-test="image-delete-button"
              class="mr-1"
              :disabled="isActiveAsset || GET_IS_CAMPAIGN_READONLY"
              @click="deleteImage"
            >
              <i
                class="fa fa-trash-can"
              />
            </b-button>
          </span>
          <b-button
            size="sm"
            variant="info"
            data-test="image-swap-button"
            :disabled="GET_IS_CAMPAIGN_READONLY"
            @click="swapImage"
          >
            <i class="fa fa-refresh" />
          </b-button>
        </div>
      </div>
    </template>
    <template v-else>
      <div
        class="loading-con mb-2"
        :style="{'aspect-ratio': aspectRatio}"
      >
        <b-spinner variant="primary" />
      </div>
    </template>
  </div>
</template>
<script>

import { mapActions, mapGetters } from 'vuex';
import * as contentTypes from '@/modules/content/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';

function initialData() {
  return {
    isLoading: true,
    imageHover: false,
    content: null,
  };
}

export default {
  name: 'RecentAsset',
  props: {
    imageName: {
      type: String,
      required: true,
    },
    creativeSizeName: {
      type: String,
      required: true,
    },
    aspectRatio: {
      type: Number,
      default: 16 / 9,
    },
    addRightPadding: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return initialData();
  },
  computed: {
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_CURRENT_ASSET_NAMES,
    ]),
    isActiveAsset() {
      return this.GET_CURRENT_ASSET_NAMES.includes(this.imageName);
    },
    payload() {
      return {
        imageName: this.imageName,
        creativeSizeName: this.creativeSizeName,
      };
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.FETCH_IMAGE_ASSET,
      contentTypes.DELETE_IMAGE_ASSET,
    ]),
    swapImage() {
      if (!this.selected || !this.GET_IS_CAMPAIGN_READONLY) this.$emit('swap', this.imageName);
    },
    deleteImage() {
      if (!this.isActiveAsset || !this.GET_IS_CAMPAIGN_READONLY) this.DELETE_IMAGE_ASSET(this.payload);
    },
  },
  created() {
    // A plain, mutable marker (rather than a native AbortController) so that a request
    // that's still queued behind the shared concurrency limit when this instance is
    // destroyed (e.g. the user switched creative) can be skipped before it's ever sent,
    // without cancelling a request that's already in flight.
    this.assetState = { isActive: true };
    this.FETCH_IMAGE_ASSET({ ...this.payload, assetState: this.assetState }).then((imageContent) => {
      this.content = `data:image/jpeg;base64,${imageContent}`;
    }).catch(() => {
      // Request was skipped (no longer active) or failed; nothing to display, and no global
      // error message is needed here.
    }).finally(() => {
      this.isLoading = false;
    });
  },
  beforeDestroy() {
    if (this.assetState) this.assetState.isActive = false;
  },
};
</script>
<style scoped lang="scss">
#selected-controls {
  position: absolute;
  z-index: 10;
  top: 0;
  right: 0;
}
#img-controls {
  position: absolute;
  z-index: 10;
  bottom: 0;
  right: 0;
}
.loading-con {
  display: flex;
  justify-content: center;
  align-items: center;
}
.check-icon {
  color: $white;
  background-color: $success;
  font-size: 1.5rem;
}
</style>
